import React, { useState, useCallback } from "react";
import {
  Box,
  Link as MuiLink,
  Table,
  TableBody,
  TableRow,
  TableCell,
} from "@mui/material";
import type { SxProps } from "@mui/material";
import { format } from "date-fns";
import { Button, ButtonVariantOption } from "@/components/Button";
import { SizingWrapperStyle } from "@/components/Wrapper";
import { EditNameModal } from "@/features/dmManagement/components/EditNameModal";
import { DesignPreviewModal } from "@/features/dmManagement/components/DesignPreviewModal";
import { PutOrderNameAPI } from "@/features/dmManagement/api/putOrderNameAPI";
import { useApiClient } from "@/hooks/useApiClient";
import type { Order } from "@/features/dmManagement/types";
import type { KeyedMutator } from "swr";
import { formatNumberWithComma } from "@/utils/formatNumber";

const thSx: SxProps = { fontWeight: "bold" } as const;

interface OrderDetailInfoProps {
  order: Order;
  mutate: KeyedMutator<Order>;
  onOpenAddressList: () => void;
}

/** 注文ステータスが「要対応」かつ宛先リストが確認待ちまたは再アップロード待ちの場合に true */
const isActionRequired = (order: Order): boolean => {
  return (
    order.orderStatus === "action_required" &&
    (order.addressList.status === "require_confirmation" ||
      order.addressList.status === "require_reupload")
  );
};

/**
 * 注文詳細情報
 * 注文のステータス・件数・金額・デザイン・宛先リストなどの詳細情報をテーブル形式で表示する
 */
export const OrderDetailInfo: React.FC<OrderDetailInfoProps> = ({
  order,
  mutate,
  onOpenAddressList,
}) => {
  const { apiClient } = useApiClient();
  const [editNameOpen, setEditNameOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  const handleSaveName = useCallback(
    async (newName: string): Promise<void> => {
      await PutOrderNameAPI(apiClient, order.id, { taskName: newName });
      await mutate();
    },
    [apiClient, order.id, mutate]
  );

  return (
    <Box>
      <Table size="small">
        <TableBody>
          <TableRow>
            <TableCell component="th" sx={{ ...thSx, width: 160 }}>
              ステータス
            </TableCell>
            <TableCell>{order.orderStatusDisplay}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell component="th" sx={thSx}>
              注文件数
            </TableCell>
            <TableCell>{formatNumberWithComma(order.quantity)}件</TableCell>
          </TableRow>
          <TableRow>
            <TableCell component="th" sx={thSx}>
              販売金額
            </TableCell>
            <TableCell>¥{formatNumberWithComma(order.price)}（税込）</TableCell>
          </TableRow>
          <TableRow>
            <TableCell component="th" sx={thSx}>
              デザインデータ
            </TableCell>
            <TableCell>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                {order.dmDesign.taskName}
                <Box component="span" sx={{ ml: 2 }}>
                  <Button
                    label="プレビュー"
                    variant={ButtonVariantOption.Outlined}
                    size={SizingWrapperStyle.INHERIT}
                    onClick={() => {
                      setPreviewOpen(true);
                    }}
                  />
                </Box>
              </Box>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell component="th" sx={thSx}>
              宛先リスト
            </TableCell>
            <TableCell>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                {order.addressList.statusDisplay}
                <Box component="span" sx={{ ml: 2 }}>
                  <Button
                    label={isActionRequired(order) ? "確認" : "表示"}
                    variant={ButtonVariantOption.Outlined}
                    size={SizingWrapperStyle.INHERIT}
                    onClick={onOpenAddressList}
                  />
                </Box>
              </Box>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell component="th" sx={thSx}>
              識別番号
            </TableCell>
            <TableCell>{order.taskId}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell component="th" sx={thSx}>
              識別名
            </TableCell>
            <TableCell>
              {order.taskName}
              <MuiLink
                component="button"
                sx={{ ml: 1 }}
                onClick={() => {
                  setEditNameOpen(true);
                }}
              >
                編集
              </MuiLink>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell component="th" sx={thSx}>
              注文日時
            </TableCell>
            <TableCell>
              {format(new Date(order.createdAt), "yyyy/MM/dd HH:mm")}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>

      <EditNameModal
        open={editNameOpen}
        onClose={() => {
          setEditNameOpen(false);
        }}
        currentName={order.taskName}
        onSave={handleSaveName}
      />

      <DesignPreviewModal
        open={previewOpen}
        onClose={() => {
          setPreviewOpen(false);
        }}
        components={order.designComponents}
      />
    </Box>
  );
};
